/* selv-ingredients.js · section « Selv · Ingrédients » (26 oct. 2026 ; revue 09) : rail, jauge, flèches, volets,
   lien #selv-formule. Le serveur rend les volets ouverts et les boutons masqués (lisible sans JS) ; init les inverse.
   fit : si le bout visible de la carte suivante fait moins de 48 px ou presque une carte entière, la largeur des
   cartes est ajustée (--sing-fit) pour en montrer 56 px ; sinon la largeur réglée est gardée.
   eq : les cartes fermées ont toutes la même hauteur (--sing-ch) ; un volet ouvert ne fait grandir que sa carte.
   SHUT (reports/ingredients/11-seo.md) : un volet fermé reçoit hidden="until-found" là où le navigateur le comprend ;
   la recherche dans la page et les liens #:~:text= (extraits Google, réponses IA) l'ouvrent alors (événement
   beforematch : le bouton passe à aria-expanded="true"). Ailleurs, hidden ordinaire, comme avant.
   Tour 1 (26 oct. 2026, reports/ingredients/integration/corrections-r1.md) :
   - DA-01 : dès 750 px, eq() donne d'abord à toutes les listes d'étiquettes la hauteur de la plus haute de la piste
     (--sing-tagh), puis égalise les cartes : étiquettes et accroches partent à la même hauteur. Au téléphone (une
     carte à la fois), --sing-tagh reste à 0.
   - PERF-CLS-ARRIVEE : un script en ligne de la section (après la jauge) pose l'état initial avant le premier
     affichage (mêmes calculs qu'ici, à garder en phase) et marque la section data-sing-pre. init() ne referme
     alors aucun volet : un lien #:~:text= a pu en ouvrir un avant ce fichier ; aria-expanded suit l'état réel.
   Vitamine E, tour 1 (27 oct. 2026, reports/ingredients/vitamine-e/corrections-1.md) :
   - VE1-01 : dès 750 px, eq() donne aussi à tous les noms la hauteur du plus haut de la piste (--sing-nameh, remise
     à 0 avant chaque mesure et au téléphone) : un nom sur 2 lignes (« Sel rose de l'Himalaya ») ne décale plus les
     étiquettes ni l'accroche de sa carte. Même calcul dans le script en ligne de la section.
   Parfum (27 oct. 2026, reports/ingredients/parfum/integration/01-construction.md ; copie d'avant :
   selv-ingredients.avant-parfum.js) : une section peut avoir deux pistes ([data-sing-rail] : ingrédients, puis notes
   du parfum). rail() règle chacune (flèches, jauge, volets, fit, eq, paint) ; layout() les traite dans l'ordre, la
   piste des notes partant de la largeur ajustée de celle des ingrédients (même largeur de carte). Égalisation des
   hauteurs par piste. HTML sans [data-sing-rail] : la section entière est une piste, comme avant.
   Couverture, corrections du tour 1 (27 oct. 2026, reports/ingredients/couverture/integration/corrections-r1.md ;
   copie d'avant : selv-ingredients.avant-couverture-r1.js) : fit() teste la largeur de contenu, gouttière de droite
   exclue (DA-COUV-03) ; même calcul dans le script en ligne de la section. Limite connue : quand les cartes ne
   tiennent pas dans V (3 cartes de 288 px : vers 890 à 935 px), la branche « k ≥ n » calcule toujours sur V et la
   piste garde un défilement d'environ 32 px, sans flèche ni jauge.
   Généralisation C3 (29 oct. 2026, reports/ingredients/generalisation/construction/C3-section.md ; copie d'avant HORS du
   thème : reports/ingredients/generalisation/archives/selv-ingredients.avant-c3-section.js) : aucun changement de code.
   La 2e piste est désormais le sous-groupe des senteurs (ingrédients odorants en h4, carte « Parfum », cartes-notes),
   ou la piste des notes hors mode séparé ; rail() la traite comme avant. Un sous-groupe sans carte (ligne « Ça sent »
   seule, huile LUV) n'a ni piste ni [data-sing-rail] : rien à régler. Pistes longues (8 à 15 cartes, bains et gels) :
   flèches d'une page de cartes, jauge et focus clavier existants ; eq() égalise aussi les cartes de fonction (div
   .sing-card__tags--none comprise dans --sing-tagh). Les rails de coffret par composant (C7) seront d'autres
   [data-sing-rail] de la même section, gérés de la même façon. */
(() => {
  if (window.__selvIng) return;
  window.__selvIng = 1;
  const d = document;
  const SHUT = 'onbeforematch' in HTMLElement.prototype ? 'until-found' : true;
  const rm = matchMedia('(prefers-reduced-motion: reduce)');
  const wide = matchMedia('(min-width: 750px)');
  const beh = () => (rm.matches ? 'auto' : 'smooth');
  const css = (el, k) => parseFloat(getComputedStyle(el)[k]) || 0;
  const PEEK = 56;
  const MIN_PEEK = 48;

  // scrollIntoView respecte le scroll-padding-top du thème (en-tête collant) : aucune marge en dur.
  const formula = (det) => {
    det.open = true;
    det.scrollIntoView({ block: 'start', behavior: beh() });
    const s = det.querySelector('summary');
    if (s) s.focus({ preventScroll: true });
  };

  // Une piste (lot PARFUM) : [data-sing-rail] avec sa piste, ses flèches, sa jauge et ses volets. lead = piste des
  // ingrédients (la première) : la piste des notes part de sa largeur ajustée (--sing-fit), même largeur de carte.
  const rail = (box, lead) => {
    const q = (s) => box.querySelector(s);
    const track = q('[data-sing-track]');
    if (!track) return null;
    const nav = q('[data-sing-nav]');
    const prev = q('[data-sing-prev]');
    const next = q('[data-sing-next]');
    const bar = q('[data-sing-bar]');
    const fill = bar && bar.firstElementChild;

    // Volets fermés, boutons visibles ; la piste n'est plus un arrêt de tabulation quand un bouton l'est.
    const btns = box.querySelectorAll('[data-sing-more]');
    if (box.hasAttribute('data-sing-pre')) {
      // État initial déjà posé par le script en ligne : aucun volet refermé (un lien #:~:text= a pu en ouvrir un).
      btns.forEach((b) => {
        const p = d.getElementById(b.getAttribute('aria-controls'));
        b.hidden = false;
        b.setAttribute('aria-expanded', String(!!p && !p.hidden));
      });
    } else {
      box.querySelectorAll('[data-sing-panel]').forEach((p) => { p.hidden = SHUT; });
      btns.forEach((b) => { b.hidden = false; b.setAttribute('aria-expanded', 'false'); });
    }
    if (btns.length) track.removeAttribute('tabindex');

    const setOff = (b, off) => {
      if (!b || b.disabled === off) return;
      const o = b === prev ? next : prev;
      if (off && o && d.activeElement === b) o.focus({ preventScroll: true });
      b.disabled = off;
    };
    let raf = 0;
    const paint = () => {
      raf = 0;
      const max = track.scrollWidth - track.clientWidth;
      const over = max - css(track, 'paddingRight') > 4;
      if (nav) nav.hidden = !over;
      if (bar) bar.hidden = !over;
      if (!over) return;
      const x = Math.max(0, Math.min(max, track.scrollLeft));
      setOff(prev, x <= 2);
      setOff(next, x >= max - 2);
      if (fill) {
        fill.style.setProperty('--w', `${((track.clientWidth / track.scrollWidth) * 100).toFixed(2)}%`);
        fill.style.setProperty('--x', `${((x / track.clientWidth) * 100).toFixed(2)}%`);
      }
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(paint); };

    const fit = () => {
      track.style.removeProperty('--sing-fit');
      const lf = lead && lead.style.getPropertyValue('--sing-fit');
      if (lf) track.style.setProperty('--sing-fit', lf);
      const it = track.firstElementChild;
      if (!it) return;
      const n = track.children.length;
      const w = it.getBoundingClientRect().width;
      const g = css(track, 'columnGap');
      const V = track.clientWidth - css(track, 'paddingLeft');
      // DA-COUV-03 : toutes les cartes doivent tenir dans la largeur de contenu C (sans la gouttière de droite), sinon
      // la dernière entre dans la gouttière et la piste défile de quelques px sans flèche ni jauge. Si elles tiennent
      // dans V mais pas dans C, elles sont réduites juste assez pour finir au bord du contenu.
      const C = V - css(track, 'paddingRight');
      const T = n * (w + g) - g;
      if (T <= C) return;
      if (T <= V) { track.style.setProperty('--sing-fit', `${((C - (n - 1) * g) / n).toFixed(1)}px`); return; }
      const p = V - Math.floor((V + g) / (w + g)) * (w + g);
      if (p >= MIN_PEEK && p <= w - MIN_PEEK) return;
      const k = Math.max(1, Math.round((V - PEEK) / (w + g)));
      const nw = k >= n ? (V - (n - 1) * g) / n : (V - PEEK) / k - g;
      track.style.setProperty('--sing-fit', `${nw.toFixed(1)}px`);
    };
    const eq = () => {
      track.style.setProperty('--sing-nameh', '0px');
      track.style.setProperty('--sing-tagh', '0px');
      if (wide.matches) {
        // VE1-01 : noms à la hauteur du plus haut de la piste (un nom sur 2 lignes ne décale plus sa carte).
        let h = 0;
        track.querySelectorAll('.sing-card__name').forEach((n) => { h = Math.max(h, n.getBoundingClientRect().height); });
        track.style.setProperty('--sing-nameh', `${Math.ceil(h)}px`);
        let t = 0;
        track.querySelectorAll('.sing-card__tags').forEach((u) => { t = Math.max(t, u.getBoundingClientRect().height); });
        track.style.setProperty('--sing-tagh', `${Math.ceil(t)}px`);
      }
      track.style.setProperty('--sing-ch', '0px');
      let m = 0;
      track.querySelectorAll('.sing-card').forEach((c) => {
        const p = c.querySelector('[data-sing-panel]');
        const open = p && !p.hidden ? p.offsetHeight + css(p, 'marginTop') : 0;
        m = Math.max(m, c.offsetHeight - open);
      });
      track.style.setProperty('--sing-ch', `${Math.ceil(m)}px`);
    };

    const step = (dir) => {
      const it = track.firstElementChild;
      const gap = css(track, 'columnGap');
      const w = it ? it.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      const per = Math.max(1, Math.floor((track.clientWidth - css(track, 'paddingLeft') + gap) / w));
      track.scrollBy({ left: dir * per * w, behavior: beh() });
    };
    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));

    track.addEventListener('focusin', (e) => {
      const it = e.target.closest('.sing__item');
      if (!it) return;
      const l = it.offsetLeft;
      const p = css(track, 'paddingLeft');
      if (l < track.scrollLeft + p - 1 || l + it.offsetWidth > track.scrollLeft + track.clientWidth + 1) {
        track.scrollTo({ left: l - p, behavior: beh() });
      }
    });
    track.addEventListener('scroll', queue, { passive: true });
    return { track, fit, eq, paint };
  };

  const init = (scope) => {
    scope.querySelectorAll('[data-sing]').forEach((sec) => {
      if (sec.dataset.singOn) return;
      sec.dataset.singOn = '1';
      const link = sec.querySelector('[data-sing-inci]');
      if (link && !d.getElementById('selv-formule')) link.hidden = true;

      // Pistes dans l'ordre du document (ingrédients, puis notes) ; HTML d'avant le lot PARFUM : la section entière.
      const boxes = sec.querySelectorAll('[data-sing-rail]');
      const rails = [];
      (boxes.length ? [...boxes] : [sec]).forEach((box) => {
        const r = rail(box, rails.length ? rails[0].track : null);
        if (r) rails.push(r);
      });
      if (!rails.length) return;
      let lraf = 0;
      // La première piste d'abord : la suivante reprend sa largeur ajustée.
      const layout = () => { lraf = 0; rails.forEach((r) => { r.fit(); r.eq(); r.paint(); }); };
      const relayout = () => { if (!lraf) lraf = requestAnimationFrame(layout); };

      sec.addEventListener('click', (e) => {
        const b = e.target.closest('[data-sing-more]');
        if (b) {
          const open = b.getAttribute('aria-expanded') !== 'true';
          const panel = d.getElementById(b.getAttribute('aria-controls'));
          b.setAttribute('aria-expanded', String(open));
          if (panel) panel.hidden = open ? false : SHUT;
          return;
        }
        const det = e.target.closest('[data-sing-inci]') && d.getElementById('selv-formule');
        if (det) {
          e.preventDefault();
          formula(det);
        }
      });

      // Volet ouvert par la recherche dans la page ou un lien #:~:text= : le navigateur retire hidden, on suit.
      sec.addEventListener('beforematch', (e) => {
        const p = e.target.closest && e.target.closest('[data-sing-panel]');
        const b = p && sec.querySelector(`[aria-controls="${p.id}"]`);
        if (b) b.setAttribute('aria-expanded', 'true');
        relayout();
      }, true);

      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(relayout);
        rails.forEach((r) => ro.observe(r.track));
      } else addEventListener('resize', relayout);
      if (d.fonts && d.fonts.ready) d.fonts.ready.then(relayout);
      layout();
    });
  };

  init(d);
  d.addEventListener('shopify:section:load', (e) => init(e.target));
  // Éditeur : choisir un bloc « Ingrédient » ramène sa carte dans le rail.
  d.addEventListener('shopify:block:select', (e) => {
    const li = e.target.closest && e.target.closest('.sing__item');
    const t = li && li.parentElement;
    if (t && t.matches('[data-sing-track]')) t.scrollTo({ left: li.offsetLeft - css(t, 'paddingLeft'), behavior: beh() });
  });
})();
